import { z } from "zod";

// ============================================================================
// INPUT SANITIZATION UTILITIES
// ============================================================================

/**
 * Sanitizes text input to prevent XSS attacks
 * - Removes HTML tags
 * - Escapes special characters
 * - Trims whitespace
 */
export const sanitizeText = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .trim()
    .replace(/<[^>]*>/g, "") // Remove HTML tags
    .replace(/[<>'"]/g, (char) => {
      // Escape special characters
      const escapeMap: Record<string, string> = {
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#x27;",
        '"': "&quot;",
      };
      return escapeMap[char] || char;
    });
};

/**
 * Validates and sanitizes URL inputs
 */
export const sanitizeUrl = (url: string | null | undefined): string => {
  if (!url) return "";
  const trimmed = url.trim();
  // Only allow http/https protocols
  if (!/^https?:\/\//i.test(trimmed)) {
    return "";
  }
  return trimmed;
};

// ============================================================================
// COMMON VALIDATION PATTERNS
// ============================================================================

const namePattern = /^[a-zA-Z\s'-]+$/;
const phonePattern = /^[\d\s\-()ext.+]+$/i;
const zipPattern = /^\d{5}(-\d{4})?$/;
const alphanumericPattern = /^[a-zA-Z0-9\s\-_.]+$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Reusable field validators
const nameValidator = z
  .string()
  .trim()
  .min(2, "Must be at least 2 characters")
  .max(100, "Must be less than 100 characters")
  .regex(namePattern, "Can only contain letters, spaces, hyphens, and apostrophes")
  .transform(sanitizeText);

const emailValidator = z
  .string()
  .trim()
  .email("Invalid email address")
  .max(255, "Email must be less than 255 characters")
  .toLowerCase()
  .refine((email) => emailPattern.test(email), "Invalid email format");

const phoneValidator = z
  .string()
  .trim()
  .regex(phonePattern, "Invalid phone number format")
  .min(10, "Phone number must be at least 10 digits")
  .max(30, "Phone number must be less than 30 characters")
  .transform((val) => val.replace(/[^\d]/g, "")); // Store only digits

const zipValidator = z
  .string()
  .trim()
  .regex(zipPattern, "Must be a valid ZIP code (e.g., 60523 or 60523-1234)");

const addressValidator = z
  .string()
  .trim()
  .max(500, "Address must be less than 500 characters")
  .transform(sanitizeText);

const notesValidator = z
  .string()
  .trim()
  .max(2000, "Notes must be less than 2000 characters")
  .transform(sanitizeText);

const currencyValidator = z
  .number()
  .positive("Amount must be positive")
  .max(999999999.99, "Amount too large")
  .multipleOf(0.01, "Amount must have at most 2 decimal places");

// ============================================================================
// PUBLIC FORMS
// ============================================================================

// Estimate Form (Public Website)
export const estimateFormSchema = z.object({
  name: nameValidator,
  email: emailValidator,
  phone: phoneValidator,
  fromZip: zipValidator,
  fromState: z.string()
    .min(2, { message: "Origin state is required" })
    .refine(val => val.length === 2, { message: "Invalid state code" }),
  toZip: zipValidator,
  toState: z.string()
    .min(2, { message: "Destination state is required" })
    .refine(val => val.length === 2, { message: "Invalid state code" }),
  moveDate: z.string().optional(),
  homeSize: z.string().optional(),
  notes: notesValidator.optional().or(z.literal("")),
});

export type EstimateFormData = z.infer<typeof estimateFormSchema>;

// ============================================================================
// ADMIN FORMS - LEADS
// ============================================================================

export const leadFormSchema = z.object({
  origin_zip: zipValidator,
  destination_zip: zipValidator,
  move_type: z.enum(["local", "interstate"]),
  contact_name: nameValidator,
  email: emailValidator.optional().or(z.literal("")),
  phone: z
    .string({ required_error: "Phone number is required" })
    .trim()
    .min(1, "Phone number is required")
    .regex(phonePattern, "Invalid phone number format")
    .max(30, "Phone number must be less than 30 characters"),
  backup_phone: z
    .string()
    .trim()
    .regex(phonePattern, "Invalid phone number format")
    .max(30, "Phone number must be less than 30 characters")
    .optional()
    .or(z.literal("")),
  status: z.enum(["lead", "quoted", "follow_up", "accepted", "paid", "job", "cancelled", "ins_claim", "closed", "bad_data", "long_date"]),
  source: z.string().optional().or(z.literal("")),
  move_date: z.string().optional().or(z.literal("")),
  pickup_date_end: z.string().optional().or(z.literal("")),
  notes: notesValidator.optional().or(z.literal("")),
});

export type LeadFormData = z.infer<typeof leadFormSchema>;

// ============================================================================
// ADMIN FORMS - CUSTOMERS
// ============================================================================

export const customerFormSchema = z.object({
  company_name: z
    .string()
    .trim()
    .max(200, "Company name must be less than 200 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  contact_name: nameValidator,
  email: emailValidator.optional().or(z.literal("")),
  phone: z
    .string()
    .trim()
    .regex(phonePattern, "Invalid phone number format")
    .max(30, "Phone number must be less than 30 characters")
    .optional()
    .or(z.literal("")),
  address: addressValidator.optional().or(z.literal("")),
  city: z
    .string()
    .trim()
    .max(100, "City must be less than 100 characters")
    .regex(/^[a-zA-Z\s\-.]+$/, "City can only contain letters, spaces, hyphens, and periods")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  state: z
    .string()
    .trim()
    .max(50, "State must be less than 50 characters")
    .regex(/^[a-zA-Z\s]+$/, "State can only contain letters and spaces")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  zip: zipValidator.optional().or(z.literal("")),
  customer_type: z.enum(["residential", "commercial"]).optional().nullable(),
  status: z.enum(["active", "inactive"]),
  notes: notesValidator.optional().or(z.literal("")),
  move_type: z.enum(["local", "interstate"]).optional().nullable(),
});

export type CustomerFormData = z.infer<typeof customerFormSchema>;

// ============================================================================
// ADMIN FORMS - JOBS
// ============================================================================

export const jobFormSchema = z.object({
  customer_id: z.string().uuid("Invalid customer ID"),
  job_number: z
    .string()
    .trim()
    .max(50, "Job number must be less than 50 characters")
    .regex(/^[A-Z0-9\-]+$/, "Job number can only contain uppercase letters, numbers, and hyphens")
    .optional(),
  job_type: z
    .string()
    .trim()
    .max(100, "Job type must be less than 100 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  status: z.enum(["scheduled", "in_progress", "completed", "cancelled"]),
  scheduled_date: z.string().optional(),
  origin_address: addressValidator.optional().or(z.literal("")),
  destination_address: addressValidator.optional().or(z.literal("")),
  crew_size: z.coerce.number().int().min(1).max(20).optional().nullable(),
  estimated_hours: z.coerce.number().positive().max(999.99).multipleOf(0.01).optional().nullable(),
  actual_hours: z.coerce.number().positive().max(999.99).multipleOf(0.01).optional().nullable(),
  special_instructions: notesValidator.optional().or(z.literal("")),
});

export type JobFormData = z.infer<typeof jobFormSchema>;

// ============================================================================
// ADMIN FORMS - QUOTES
// ============================================================================

export const quoteFormSchema = z.object({
  customer_id: z.string().uuid("Invalid customer ID").optional(),
  lead_id: z.string().uuid("Invalid lead ID").optional(),
  quote_number: z
    .string()
    .trim()
    .max(50, "Quote number must be less than 50 characters")
    .regex(/^[A-Z0-9\-]+$/, "Quote number can only contain uppercase letters, numbers, and hyphens")
    .optional(),
  status: z.enum(["draft", "sent", "accepted", "rejected"]),
  subtotal: currencyValidator,
  tax: currencyValidator,
  total_amount: currencyValidator,
  deposit_amount: currencyValidator.optional().nullable(),
  valid_until: z.string().optional(),
  notes: notesValidator.optional().or(z.literal("")),
}).refine(
  (data) => data.customer_id || data.lead_id,
  "Either customer_id or lead_id must be provided"
);

export type QuoteFormData = z.infer<typeof quoteFormSchema>;

// ============================================================================
// ADMIN FORMS - INVOICES
// ============================================================================

export const invoiceFormSchema = z.object({
  customer_id: z.string().uuid("Invalid customer ID"),
  job_id: z.string().uuid("Invalid job ID").optional(),
  invoice_number: z
    .string()
    .trim()
    .max(50, "Invoice number must be less than 50 characters")
    .regex(/^[A-Z0-9\-]+$/, "Invoice number can only contain uppercase letters, numbers, and hyphens")
    .optional(),
  status: z.enum(["draft", "sent", "paid", "cancelled"]),
  payment_status: z.enum(["unpaid", "partial", "paid"]),
  subtotal: currencyValidator,
  tax: currencyValidator,
  total: currencyValidator,
  amount_paid: currencyValidator.optional().default(0),
  due_date: z.string().optional(),
  paid_date: z.string().optional().nullable(),
  payment_method: z
    .string()
    .trim()
    .max(50, "Payment method must be less than 50 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  notes: notesValidator.optional().or(z.literal("")),
});

export type InvoiceFormData = z.infer<typeof invoiceFormSchema>;

// ============================================================================
// ADMIN FORMS - PAYMENTS
// ============================================================================

export const paymentFormSchema = z.object({
  invoice_id: z.string().uuid("Invalid invoice ID"),
  amount: currencyValidator,
  payment_date: z.string(),
  payment_method: z
    .string()
    .trim()
    .max(50, "Payment method must be less than 50 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  transaction_id: z
    .string()
    .trim()
    .max(200, "Transaction ID must be less than 200 characters")
    .regex(alphanumericPattern, "Transaction ID can only contain letters, numbers, spaces, hyphens, underscores, and periods")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  notes: notesValidator.optional().or(z.literal("")),
});

export type PaymentFormData = z.infer<typeof paymentFormSchema>;

// ============================================================================
// ADMIN FORMS - FLEET
// ============================================================================

export const fleetFormSchema = z.object({
  truck_number: z
    .string()
    .trim()
    .min(1, "Truck number is required")
    .max(50, "Truck number must be less than 50 characters")
    .regex(alphanumericPattern, "Truck number can only contain letters, numbers, spaces, hyphens, underscores, and periods")
    .transform(sanitizeText),
  type: z
    .string()
    .trim()
    .max(100, "Type must be less than 100 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  license_plate: z
    .string()
    .trim()
    .max(20, "License plate must be less than 20 characters")
    .regex(/^[A-Z0-9\s\-]+$/i, "License plate can only contain letters, numbers, spaces, and hyphens")
    .transform((val) => val.toUpperCase())
    .optional()
    .or(z.literal("")),
  capacity_cuft: z.coerce.number().positive().max(9999.99).multipleOf(0.01).optional().nullable(),
  status: z.enum(["available", "in_use", "maintenance", "retired"]),
  current_location: addressValidator.optional().or(z.literal("")),
});

export type FleetFormData = z.infer<typeof fleetFormSchema>;

// ============================================================================
// ADMIN FORMS - REVIEWS
// ============================================================================

export const reviewFormSchema = z.object({
  customer_id: z.string().uuid("Invalid customer ID"),
  job_id: z.string().uuid("Invalid job ID").optional().nullable(),
  rating: z.coerce.number().int().min(1, "Rating must be at least 1").max(5, "Rating must be at most 5"),
  review_text: z
    .string()
    .trim()
    .min(10, "Review must be at least 10 characters")
    .max(5000, "Review must be less than 5000 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  would_recommend: z.boolean().default(true),
  service_quality: z.coerce.number().int().min(1).max(5).optional().nullable(),
  professionalism: z.coerce.number().int().min(1).max(5).optional().nullable(),
  timeliness: z.coerce.number().int().min(1).max(5).optional().nullable(),
  is_public: z.boolean().default(false),
  is_featured: z.boolean().default(false),
});

export type ReviewFormData = z.infer<typeof reviewFormSchema>;

// ============================================================================
// ADMIN FORMS - KNOWLEDGE BASE
// ============================================================================

export const knowledgeBaseFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(3, "Title must be at least 3 characters")
    .max(200, "Title must be less than 200 characters")
    .transform(sanitizeText),
  content: z
    .string()
    .trim()
    .min(10, "Content must be at least 10 characters")
    .max(50000, "Content must be less than 50000 characters"),
  category: z
    .string()
    .trim()
    .max(100, "Category must be less than 100 characters")
    .transform(sanitizeText)
    .optional()
    .or(z.literal("")),
  is_active: z.boolean().default(true),
  is_public: z.boolean().default(false),
  order_index: z.coerce.number().int().min(0).max(9999).default(0),
});

export type KnowledgeBaseFormData = z.infer<typeof knowledgeBaseFormSchema>;

// ============================================================================
// COMPANY SCHEMAS
// ============================================================================

// Company contact info with AI-friendly facts
export const COMPANY_INFO = {
  name: "LifeTime Movers",
  legalName: "LifeTime Movers LLC",
  foundingYear: "2018",
  url: "https://www.lifetimemovers.us",
  logo: "https://www.lifetimemovers.us/logo.png",
  image: "https://www.lifetimemovers.us/og-image.jpg",
  phone: "(630) 686-9987",
  email: "info@lifetimemovers.us",
  address: {
    street: "2021 Midwest Rd Ste 200",
    city: "Oak Brook",
    state: "IL",
    zip: "60523",
    country: "US"
  },
  coordinates: {
    latitude: "41.8369",
    longitude: "-87.9217"
  },
  hours: "Mon-Fri 9:00-18:00, Sat 11:00-16:00",
  priceRange: "$",
  reviewCount: "5000",
  ratingValue: "4.9",
  satisfactionRate: "98",
  social: {
    facebook: "https://www.facebook.com/people/LifeTime-Movers/61558861722325/",
    linkedin: "https://www.linkedin.com/in/lifetime-movers-chicago-53338331b/",
    youtube: "https://www.youtube.com/@lifetimemoverschicago"
  },
  // AI-friendly quotable facts
  facts: {
    movesCompleted: "5000+",
    satisfactionRate: "98%",
    yearsInBusiness: "7",
    pickupStates: ["Illinois", "Indiana", "Wisconsin", "Michigan"],
    deliveryStates: "all 48 contiguous US states",
    headquartersCity: "Oak Brook, Illinois",
    serviceRadius: "50 miles for local moves",
    freeStorage: "30 days",
    includedInsurance: "$10,000 standard freight moving insurance",
    localRateStarting: "$150/hour for 2 men + 1 truck",
    interstateMinimum: "300 cubic feet"
  }
};

// Local service areas
export const LOCAL_SERVICE_AREAS = [
  // Chicago neighborhoods
  { name: "Lakeview, Chicago", type: "Neighborhood" },
  { name: "Lincoln Park, Chicago", type: "Neighborhood" },
  { name: "West Loop, Chicago", type: "Neighborhood" },
  { name: "Bucktown, Chicago", type: "Neighborhood" },
  
  // North Shore suburbs
  { name: "Inverness, IL", type: "City" },
  { name: "Northfield, IL", type: "City" },
  { name: "Lincolnshire, IL", type: "City" },
  { name: "Deerfield, IL", type: "City" },
  { name: "Deer Park, IL", type: "City" },
  { name: "Kildeer, IL", type: "City" },
  { name: "Winnetka, IL", type: "City" },
  { name: "Wilmette, IL", type: "City" },
  { name: "Kenilworth, IL", type: "City" },
  { name: "Glencoe, IL", type: "City" },
  
  // General areas
  { name: "Oak Brook, IL", type: "City" },
  { name: "Chicago, IL", type: "City" },
];

// Blog Collection Page Schema
export const generateBlogCollectionSchema = (posts: Array<{ title: string; slug: string; excerpt?: string | null; published_at?: string | null; featured_image_url?: string | null }>) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": "https://www.lifetimemovers.us/blog#webpage",
  "name": "LifeTime Movers Blog",
  "description": "Moving Companies Chicago Area; Expert moving tips, comprehensive guides, and industry insights for local and long distance home moving services.",
  "url": "https://www.lifetimemovers.us/blog",
  "isPartOf": {
    "@id": "https://www.lifetimemovers.us/#website"
  },
  "publisher": {
    "@type": "Organization",
    "name": "LifeTime Movers",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.lifetimemovers.us/logo.png"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "5000",
      "bestRating": "5",
      "worstRating": "1"
    }
  },
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": posts.slice(0, 10).map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `https://www.lifetimemovers.us/blog/${post.slug}`,
      "name": post.title
    }))
  }
});

// 1. Organization Schema (Homepage) - Enhanced for AI Search
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "MovingCompany",
  "@id": `${COMPANY_INFO.url}/#organization`,
  "name": COMPANY_INFO.name,
  "legalName": COMPANY_INFO.legalName,
  "foundingDate": COMPANY_INFO.foundingYear,
  "slogan": "We do the heavy lifting so you don't have to",
  "description": "LifeTime Movers is Chicago's most trusted interstate and local moving company with a 98% customer satisfaction rate. Based in Oak Brook, Illinois, we have successfully completed over 10,000 moves since 2018. We pick up from Illinois, Indiana, Wisconsin, and Michigan with delivery to all 48 continental US states. Every move includes professional disassembly/reassembly, premium packing materials, 30 days free climate-controlled storage, and $10,000 DOT-required freight insurance at no extra cost.",
  "disambiguatingDescription": "LifeTime Movers (USDOT 3733668) is an independent moving company headquartered in Oak Brook, Illinois. We are NOT affiliated with Lifetime Moving & Storage (Arizona), Lifetime Moving Systems (Massachusetts), or any other similarly-named moving companies. Our service area is limited to pickups from Illinois, Indiana, Wisconsin, and Michigan.",
  "knowsAbout": [
    "Local moving services in Chicago metropolitan area",
    "Interstate moving from Midwest to all 48 states",
    "Professional packing and unpacking services",
    "Furniture disassembly and reassembly",
    "Climate-controlled storage solutions",
    "Corporate and residential relocations",
    "Specialty item moving including pianos and antiques"
  ],
  "url": COMPANY_INFO.url,
  "logo": COMPANY_INFO.logo,
  "image": COMPANY_INFO.image,
  "telephone": COMPANY_INFO.phone,
  "email": COMPANY_INFO.email,
  "priceRange": COMPANY_INFO.priceRange,
  "currenciesAccepted": "USD",
  "paymentAccepted": "Credit Card, Debit Card, Cash, Zelle, Venmo, Cash App",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": COMPANY_INFO.address.street,
    "addressLocality": COMPANY_INFO.address.city,
    "addressRegion": COMPANY_INFO.address.state,
    "postalCode": COMPANY_INFO.address.zip,
    "addressCountry": COMPANY_INFO.address.country
  },
  "location": {
    "@type": "Place",
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": COMPANY_INFO.coordinates.latitude,
      "longitude": COMPANY_INFO.coordinates.longitude
    }
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "11:00",
      "closes": "16:00"
    }
  ],
  "areaServed": [
    {
      "@type": "State",
      "name": "Illinois",
      "description": "Full local and interstate moving services throughout the Chicago metropolitan area and all Illinois cities"
    },
    {
      "@type": "State",
      "name": "Indiana",
      "description": "Interstate pickup services from all Indiana cities with delivery nationwide"
    },
    {
      "@type": "State",
      "name": "Wisconsin",
      "description": "Interstate pickup services from all Wisconsin cities with delivery nationwide"
    },
    {
      "@type": "State",
      "name": "Michigan",
      "description": "Interstate pickup services from all Michigan cities with delivery nationwide"
    },
    {
      "@type": "Country",
      "name": "United States",
      "description": "Delivery to all 48 contiguous US states from our Midwest service area"
    }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": COMPANY_INFO.ratingValue,
    "reviewCount": COMPANY_INFO.reviewCount,
    "bestRating": "5",
    "worstRating": "1"
  },
  "award": "98% Customer Satisfaction Rate",
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "DOT Licensed Interstate Moving Company"
    }
  ],
  "sameAs": [
    COMPANY_INFO.social.facebook,
    COMPANY_INFO.social.linkedin,
    COMPANY_INFO.social.youtube,
    "https://www.yelp.com/biz/lifetime-movers-oak-brook",
    "https://www.bbb.org/us/il/oak-brook/profile/moving-companies/lifetime-movers-0654-88752857"
  ],
  "identifier": [
    {
      "@type": "PropertyValue",
      "name": "USDOT",
      "value": "3733668"
    },
    {
      "@type": "PropertyValue",
      "name": "MC",
      "value": "1318959"
    }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Moving Services by Location",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Naperville Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Naperville", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Elmhurst Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Elmhurst", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Hinsdale Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Hinsdale", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Schaumburg Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Schaumburg", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Bolingbrook Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Bolingbrook", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Oak Brook Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Oak Brook", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Downers Grove Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Downers Grove", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Oak Park Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Oak Park", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Oak Lawn Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Oak Lawn", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "DeKalb Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "DeKalb", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Lombard Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Lombard", "addressRegion": "IL" } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Vernon Hills Movers", "serviceType": "Moving Services", "areaServed": { "@type": "City", "name": "Vernon Hills", "addressRegion": "IL" } } }
    ]
  }
};

// Customer Review Schema - AI-friendly testimonials for rich snippets
export const customerReviewsSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": COMPANY_INFO.name,
  "url": COMPANY_INFO.url,
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": COMPANY_INFO.ratingValue,
    "reviewCount": COMPANY_INFO.reviewCount,
    "bestRating": "5",
    "worstRating": "1"
  },
  "review": [
    {
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Sarah M."
      },
      "datePublished": "2024-11-15",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "reviewBody": "Best moving experience in Chicago! LifeTime Movers quoted us exactly what we paid - no surprises. The crew was professional, careful with our furniture, and finished ahead of schedule. Highly recommend for anyone moving in the Chicago area."
    },
    {
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Michael T."
      },
      "datePublished": "2024-10-22",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "reviewBody": "We moved from Chicago to Los Angeles and couldn't be happier. The transparent cubic feet pricing meant we knew our costs upfront. Our belongings arrived safely and on time. The 30 days free storage was a lifesaver while waiting for our new home."
    },
    {
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Jennifer L."
      },
      "datePublished": "2024-09-08",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "reviewBody": "The movers were incredibly careful with my grandmother's antique furniture. They wrapped everything properly, disassembled and reassembled the beds perfectly, and were so polite and professional. This is how moving should be done!"
    },
    {
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "David R."
      },
      "datePublished": "2024-08-14",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "reviewBody": "Local move from Lincoln Park to Oak Brook. The 3-man crew arrived on time, worked efficiently, and handled our piano move without any issues. Fair pricing at $200/hour with no hidden fees. Will definitely use again!"
    },
    {
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Amanda K."
      },
      "datePublished": "2024-07-30",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "reviewBody": "Outstanding service from quote to delivery. The VIBE chat helped me get an instant quote online, and the price was exactly what I paid. The included insurance gave me peace of mind. 98% satisfaction rate is well deserved!"
    }
  ]
};

// 2. LocalBusiness Schema with Service Radius - Enhanced for AI Search
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${COMPANY_INFO.url}/#localbusiness`,
  "name": COMPANY_INFO.name,
  "description": "LifeTime Movers provides professional moving services from our Oak Brook, Illinois headquarters. With a 98% satisfaction rate and over 10,000 successful moves, we offer local moving starting at $150/hour and interstate moving with transparent cubic foot pricing. Our service includes free packing materials, furniture protection, and 30 days complimentary storage.",
  "url": COMPANY_INFO.url,
  "telephone": COMPANY_INFO.phone,
  "email": COMPANY_INFO.email,
  "priceRange": "$$",
  "image": "https://www.lifetimemovers.us/images/logo-email.jpg",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": COMPANY_INFO.address.street,
    "addressLocality": COMPANY_INFO.address.city,
    "addressRegion": COMPANY_INFO.address.state,
    "postalCode": COMPANY_INFO.address.zip,
    "addressCountry": COMPANY_INFO.address.country
  },
  "location": {
    "@type": "Place",
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": COMPANY_INFO.coordinates.latitude,
      "longitude": COMPANY_INFO.coordinates.longitude
    }
  },
  "areaServed": [
    { "@type": "City", "name": "Chicago", "sameAs": "https://en.wikipedia.org/wiki/Chicago", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Naperville", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Hinsdale", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Oak Brook", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Elmhurst", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Schaumburg", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Bolingbrook", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Downers Grove", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Oak Park", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Oak Lawn", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Lombard", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Aurora", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Wheaton", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Arlington Heights", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Evanston", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Skokie", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Berwyn", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Cicero", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Orland Park", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "City", "name": "Tinley Park", "containedInPlace": { "@type": "State", "name": "Illinois" } },
    { "@type": "State", "name": "Illinois" },
    { "@type": "State", "name": "Indiana" },
    { "@type": "State", "name": "Wisconsin" },
    { "@type": "State", "name": "Michigan" }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "LifeTime Movers Moving Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Local Moving Services",
          "description": "Hourly local moving services in Chicago and suburbs. 2 men + 1 truck starts at $150/hour. Includes pad wrapping, blanket protection, furniture disassembly/reassembly, and all packing materials. No hidden fees - what we quote is what you pay."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Long Distance Moving",
          "description": "Interstate moving from Illinois, Indiana, Wisconsin, and Michigan to all 48 contiguous states. Pricing based on cubic footage with a 300 CF minimum. Includes 15% fuel surcharge, volume discounts up to 20% for larger moves, and guaranteed quotes for price certainty."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Professional Packing Services",
          "description": "Full-service packing at $1.00-$1.25 per cubic foot. Our trained packers use quality materials to protect fragile items, artwork, and electronics. Includes boxes, tape, bubble wrap, and specialty containers for dishes and wardrobe."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Free Storage",
          "description": "30 days of complimentary climate-controlled storage included with every move. Perfect for flexible closing dates, home staging, or transitional periods between residences."
        }
      }
    ]
  }
};

// 3. Service Schema for Local Services - Enhanced for AI Search
export const localServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY_INFO.url}/#localservice`,
  "serviceType": "Local Moving Services",
  "provider": {
    "@id": `${COMPANY_INFO.url}/#organization`
  },
  "areaServed": LOCAL_SERVICE_AREAS.map(area => ({
    "@type": "Place",
    "name": area.name
  })),
  "description": "LifeTime Movers provides hourly local moving services throughout the Chicago metropolitan area. Our experienced crews handle everything from studio apartments to 5+ bedroom homes. Service includes furniture disassembly/reassembly, pad wrapping, blanket protection, and careful handling of fragile items. We serve all Chicago neighborhoods including Lakeview, Lincoln Park, West Loop, Bucktown, and North Shore suburbs like Winnetka, Wilmette, Deerfield, and Oak Brook.",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": "150",
    "highPrice": "650",
    "offerCount": "8",
    "description": "Hourly rates from $150 (2 men + 1 truck) to $650 (6 men + 2 trucks). 3-hour minimum applies."
  }
};

// 4. Service Schema for Nationwide Services - Enhanced for AI Search
export const nationwideServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY_INFO.url}/#nationwideservice`,
  "serviceType": "Long Distance Moving",
  "provider": {
    "@id": `${COMPANY_INFO.url}/#organization`
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "description": "LifeTime Movers provides interstate moving from our service area in Illinois, Indiana, Wisconsin, and Michigan to all 48 contiguous US states. Our long distance moves include guaranteed quotes, proactive status updates, door-to-door delivery, and $10,000 DOT-required freight insurance. Volume discounts available: 10% off for moves up to 500 CF, 15% for 501-1000 CF, 20% for 1001-1500 CF, and 20% for moves over 1500 CF.",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": "1500",
    "highPrice": "15000",
    "description": "Pricing based on cubic footage and destination. 300 CF minimum. Includes fuel surcharge and volume discounts."
  }
};

// WebSite Schema with SearchAction for Google sitelinks searchbox
export const websiteSearchActionSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": COMPANY_INFO.name,
  "url": COMPANY_INFO.url,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${COMPANY_INFO.url}/service-areas?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

// 5. BreadcrumbList Schema (static - for homepage)
export const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": COMPANY_INFO.url
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": `${COMPANY_INFO.url}#services`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Service Areas",
      "item": `${COMPANY_INFO.url}#areas`
    }
  ]
};

// 5b. Dynamic BreadcrumbList Schema Generator
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "name": `${items[items.length - 1]?.name || 'Page'} Navigation Path`,
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "WebPage",
      "@id": item.url,
      "name": item.name
    }
  }))
});

// 6. FAQPage Schema - Enhanced for AI Search with comprehensive answers
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "LifeTime Movers FAQ - Your Moving Questions Answered",
  "description": "Comprehensive answers to common questions about LifeTime Movers services, pricing, coverage areas, licensing, and what's included with every move.",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Do you provide nationwide moving services?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, LifeTime Movers provides professional moving services from Illinois, Indiana, Wisconsin, and Michigan to all 48 contiguous US states. We offer door-to-door delivery service with proactive status updates, guaranteed quotes for price certainty, and $10,000 DOT-required freight insurance included at no extra cost."
      }
    },
    {
      "@type": "Question",
      "name": "What areas in Chicago do you serve?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We serve all Chicago neighborhoods including Lakeview, Lincoln Park, West Loop, Bucktown, and downtown Chicago, plus Oak Brook and North Shore suburbs including Winnetka, Wilmette, Kenilworth, Glencoe, Deerfield, Northfield, Lincolnshire, Inverness, Kildeer, and Deer Park. Our local service covers a 50-mile radius from our Oak Brook headquarters."
      }
    },
    {
      "@type": "Question",
      "name": "Are you licensed and insured?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, LifeTime Movers is fully DOT licensed for interstate moving and carries comprehensive insurance. Every move includes $10,000 standard freight moving insurance at no additional cost. We are trusted by over 10,000 families with a 98% customer satisfaction rate since 2018."
      }
    },
    {
      "@type": "Question",
      "name": "What's included in your moving prices?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Every LifeTime Movers move includes: professional disassembly and reassembly of furniture, pad wrapping and blanket protection, all packing materials for furniture protection, 30 days free climate-controlled storage, and $10,000 DOT-required freight insurance. No hidden fees - what we quote is what you pay."
      }
    },
    {
      "@type": "Question",
      "name": "How much does local moving cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Local moving rates start at $150/hour for 2 men and 1 truck. Larger moves with more crew and trucks range up to $650/hour for 6 men and 2 trucks. A 3-hour minimum applies. Price includes all packing materials, furniture protection, and disassembly/reassembly."
      }
    },
    {
      "@type": "Question",
      "name": "How is long distance moving priced?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Interstate moving is priced by cubic footage with a 300 CF minimum. Pricing includes a 15% fuel surcharge calculated before discounts. Volume discounts apply automatically: 10% off for moves up to 500 CF, 15% for 501-1000 CF, 20% for 1001-1500 CF, and 20% for moves over 1500 CF."
      }
    }
  ]
};

// Individual Service Schemas with Offers - Enhanced for AI Search
export const localMovingServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY_INFO.url}/moving-services#local-moving`,
  "name": "Local Moving Services Chicago - LifeTime Movers",
  "serviceType": "Local Moving",
  "provider": {
    "@type": "MovingCompany",
    "@id": `${COMPANY_INFO.url}/#organization`
  },
  "areaServed": LOCAL_SERVICE_AREAS.map(area => ({
    "@type": "Place",
    "name": area.name
  })),
  "description": "LifeTime Movers provides hourly local moving services throughout the Chicago metropolitan area and surrounding suburbs. Our professional crews are trained in proper lifting techniques, furniture handling, and fragile item protection. Every local move includes furniture disassembly and reassembly, pad wrapping, blanket protection, and floor/doorway protection. We handle everything from studio apartments to large homes with multiple floors. Available 6 days a week with same-week booking available.",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": "150",
    "highPrice": "650",
    "offerCount": "8",
    "priceValidUntil": new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0],
    "itemOffered": [
      { "@type": "Service", "name": "2 men + 1 truck", "description": "$150/hour - Perfect for studios and 1-bedroom moves" },
      { "@type": "Service", "name": "3 men + 1 truck", "description": "$200/hour - Ideal for 2-3 bedroom homes" },
      { "@type": "Service", "name": "4 men + 1 truck", "description": "$280/hour - Great for larger 3-bedroom homes" },
      { "@type": "Service", "name": "4 men + 2 trucks", "description": "$480/hour - Required for moves over 1500 cubic feet" }
    ]
  },
  "termsOfService": "https://www.lifetimemovers.us/terms"
};

export const longDistanceServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY_INFO.url}/moving-services#long-distance`,
  "name": "Long Distance Interstate Moving - LifeTime Movers",
  "serviceType": "Interstate Moving",
  "provider": {
    "@type": "MovingCompany",
    "@id": `${COMPANY_INFO.url}/#organization`
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States",
    "containsPlace": [
      { "@type": "State", "name": "All 48 Contiguous States" }
    ]
  },
  "description": "LifeTime Movers provides professional interstate moving from Illinois, Indiana, Wisconsin, and Michigan to all 48 contiguous US states. Our long distance service includes guaranteed quotes so the price you're quoted is the price you pay, regular status updates of your shipment, door-to-door delivery with inside placement, and $10,000 DOT-required freight insurance included. We offer volume discounts up to 20% for larger moves and flexible delivery windows. Popular routes include Chicago to Los Angeles, Chicago to New York, Chicago to Florida, and Chicago to Texas.",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": "1500",
    "highPrice": "15000",
    "offerCount": "48",
    "description": "Prices calculated by cubic footage and distance. 300 CF minimum. Volume discounts: 10% (0-500 CF), 15% (501-1000 CF), 20% (1001-1500 CF), 20% (1501+ CF). Includes 15% fuel surcharge and all standard services."
  },
  "termsOfService": "https://www.lifetimemovers.us/terms"
};

export const packingServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY_INFO.url}/moving-services#packing`,
  "name": "Professional Packing Services - LifeTime Movers",
  "serviceType": "Packing and Unpacking",
  "provider": {
    "@type": "MovingCompany",
    "@id": `${COMPANY_INFO.url}/#organization`
  },
  "areaServed": LOCAL_SERVICE_AREAS.map(area => ({
    "@type": "Place",
    "name": area.name
  })),
  "description": "LifeTime Movers offers professional packing and unpacking services to protect your belongings and save you time. Our trained packers use high-quality materials including double-walled boxes, acid-free paper, bubble wrap, and specialty containers for dishes, artwork, and electronics. We handle everything from fragile china to bulky furniture with care. Packing services include labeling by room for easy unpacking. Available as full-pack, partial-pack, or fragile-only options.",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": "1.00",
    "highPrice": "1.25",
    "description": "$1.25/cubic foot for moves up to 501 CF, $1.00/cubic foot for larger moves. Includes all materials and labor."
  },
  "termsOfService": "https://www.lifetimemovers.us/terms"
};

export const storageServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY_INFO.url}/moving-services#storage`,
  "name": "Free Moving Storage - LifeTime Movers",
  "serviceType": "Climate-Controlled Storage",
  "provider": {
    "@type": "MovingCompany",
    "@id": `${COMPANY_INFO.url}/#organization`
  },
  "description": "LifeTime Movers includes 30 days of free climate-controlled storage with every move at no additional cost. Our secure storage facility maintains consistent temperature and humidity to protect furniture, electronics, and personal belongings. Perfect for flexible closing dates, home staging, renovations, or transitional periods between residences. Extended storage available at competitive monthly rates for longer needs.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "30 days FREE climate-controlled storage included with every move. Extended storage available at competitive rates.",
    "availability": "https://schema.org/InStock"
  },
  "termsOfService": "https://www.lifetimemovers.us/terms"
};

// Services Page Schema - Combined for optimal SEO
export const servicesPageSchema = [
  organizationSchema,
  localMovingServiceSchema,
  longDistanceServiceSchema,
  packingServiceSchema,
  storageServiceSchema
];

// Service Areas Page Schema
export const serviceAreasPageSchema = [
  organizationSchema,
  localServiceSchema,
  nationwideServiceSchema,
  {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": "Chicago Service Area",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": ["IL", "IN", "WI", "MI"]
    }
  }
];

// Contact Page Schema
export const contactPageSchema = [
  organizationSchema,
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@id": `${COMPANY_INFO.url}/#organization`
    }
  }
];

// ============================================================================
// REVIEW SCHEMAS FOR SEO
// ============================================================================

export interface GoogleReview {
  id: string;
  reviewer_name: string;
  rating: number;
  review_text: string;
  review_date: string;
  source: string;
  move_type?: string;
  is_public?: boolean;  // Optional - not included in public view (all records are already public)
  is_featured?: boolean;  // Optional - may not be needed in all contexts
}

/**
 * Generate Review schema entities for SEO rich snippets
 * @param reviews - Array of reviews from google_reviews table
 * @returns Array of Review schema objects
 */
export const generateReviewsSchema = (reviews: GoogleReview[]) => {
  return reviews.map(review => ({
    "@type": "Review",
    "author": {
      "@type": "Person",
      "name": review.reviewer_name
    },
    "datePublished": review.review_date,
    "reviewBody": review.review_text,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.rating.toString(),
      "bestRating": "5",
      "worstRating": "1"
    },
    "itemReviewed": {
      "@type": "MovingCompany",
      "name": COMPANY_INFO.name,
      "@id": `${COMPANY_INFO.url}/#organization`
    }
  }));
};

/**
 * Generate organizationSchema with embedded reviews for rich snippets
 * @param reviews - Array of featured reviews from google_reviews table  
 * @returns Organization schema with review entities embedded
 */
export const generateOrganizationSchemaWithReviews = (reviews: GoogleReview[]) => {
  const reviewSchemas = generateReviewsSchema(reviews);
  
  return {
    ...organizationSchema,
    "review": reviewSchemas
  };
};

/**
 * Generate location-specific review schema for rich snippets
 * @param city - City name for the location
 * @param stateAbbr - State abbreviation
 * @returns MovingCompany schema with aggregate rating and sample reviews
 */
export const generateLocationReviewSchema = (city: string, stateAbbr: string) => ({
  "@context": "https://schema.org",
  "@type": "MovingCompany",
  "name": `LifeTime Movers - ${city}, ${stateAbbr}`,
  "url": `https://www.lifetimemovers.us/service-areas/movers/${city.toLowerCase().replace(/\s+/g, '-')}`,
  "telephone": COMPANY_INFO.phone,
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "5000",
    "bestRating": "5",
    "worstRating": "1"
  },
  "review": [
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": `${city} Customer` },
      "datePublished": "2025-12-15",
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "reviewBody": `Excellent experience moving from ${city}. Professional crew, on-time arrival, careful with all our belongings. Highly recommend LifeTime Movers!`
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": `${city} Family` },
      "datePublished": "2025-11-22",
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "reviewBody": `Best movers in ${city}! The team was efficient, friendly, and took great care of our furniture. The free 30-day storage was a huge bonus.`
    }
  ]
});
